import { HttpService } from '@/data/http/http-service';
import { QuestionBuildingHeatingDto } from '@/data/http/impact-co2/questions/building-heating/dto.type.ts';
import { QuestionBuildingHeatingMapper } from '@/data/http/impact-co2/questions/building-heating/mapper.ts';
import { QuestionCargoDto } from '@/data/http/impact-co2/questions/cargo/dto.type.ts';
import { QuestionCargoMapper } from '@/data/http/impact-co2/questions/cargo/mapper.ts';
import { QuestionDigitalDto } from '@/data/http/impact-co2/questions/digital/dto.type.ts';
import { QuestionDigitalMapper } from '@/data/http/impact-co2/questions/digital/mapper.ts';
import { QuestionOfficeSuppliesNewDto } from '@/data/http/impact-co2/questions/office-supplies-new/dto.type.ts';
import { QuestionOfficeSuppliesNewMapper } from '@/data/http/impact-co2/questions/office-supplies-new/mapper.ts';
import { QuestionOfficeSuppliesRefurbishDto } from '@/data/http/impact-co2/questions/office-supplies-refurbish/dto.type.ts';
import { QuestionOfficeSuppliesRefurbishMapper } from '@/data/http/impact-co2/questions/office-supplies-refurbish/mapper.ts';
import { QuestionScenographyNoRecyclableDto } from '@/data/http/impact-co2/questions/scenography-no-recyclable/dto.type.ts';
import { QuestionScenographyNoRecyclableMapper } from '@/data/http/impact-co2/questions/scenography-no-recyclable/mapper.ts';
import { QuestionScenographyRecyclableDto } from '@/data/http/impact-co2/questions/scenography-recyclable/dto.type.ts';
import { QuestionScenographyRecyclableMapper } from '@/data/http/impact-co2/questions/scenography-recyclable/mapper.ts';
import { QuestionTeamMobilityDto } from '@/data/http/impact-co2/questions/team-mobility/dto.type.ts';
import { QuestionTeamMobilityMapper } from '@/data/http/impact-co2/questions/team-mobility/mapper.ts';
import {
  ImpactCo2Answer,
  ValuesAccommodation,
  ValuesAccommodationPublic,
  ValuesBuilding,
  ValuesBuildingHeating,
  ValuesCargo,
  ValuesCateringBeverage,
  ValuesCateringMeal,
  ValuesDigital,
  ValuesEnergy,
  ValuesEquipment,
  ValuesGenerator,
  ValuesMobility,
  ValuesMobilityPublic,
  ValuesNonRecyclableWaste,
  ValuesOfficeSuppliesNew,
  ValuesOfficeSuppliesRefurbish,
  ValuesRecyclableWaste,
  ValuesScenographyNoRecyclable,
  ValuesScenographyRecyclable,
  ValuesTeamMobility,
  ValuesWater,
} from '@/domain/impact-co2/entities/question.entity';

import { QuestionAccommodationDto } from './accommodation/dto.type';
import { QuestionAccommodationMapper } from './accommodation/mapper';
import { QuestionAccommodationPublicDto } from './accommodation-public/dto.type';
import { QuestionAccommodationPublicMapper } from './accommodation-public/mapper';
import { QuestionBuildingDto } from './building/dto.type';
import { QuestionBuildingMapper } from './building/mapper';
import { QuestionCateringBeverageDto } from './catering-beverage/dto.type';
import { QuestionCateringBeverageMapper } from './catering-beverage/mapper';
import { QuestionCateringMealDto } from './catering-meal/dto.type';
import { QuestionCateringMealMapper } from './catering-meal/mapper';
import { QuestionEnergyDto } from './energy/dto.type';
import { QuestionEnergyMapper } from './energy/mapper';
import { QuestionEquipmentDto } from './equipment/dto.type';
import { QuestionEquipmentMapper } from './equipment/mapper';
import { QuestionGeneratorDto } from './generator/dto.type';
import { QuestionGeneratorMapper } from './generator/mapper';
import { QuestionMobilityDto } from './mobility/dto.type';
import { QuestionMobilityMapper } from './mobility/mapper';
import { QuestionMobilityPublicDto } from './mobility-public/dto.type';
import { QuestionMobilityPublicMapper } from './mobility-public/mapper';
import { QuestionNonRecyclableWasteDto } from './non-recyclable-waste/dto.type';
import { QuestionNonRecyclableWasteMapper } from './non-recyclable-waste/mapper';
import { QuestionRecyclableWasteDto } from './recyclable-waste/dto.type';
import { QuestionRecyclableWasteMapper } from './recyclable-waste/mapper';
import { buildUrl } from './utils/build-url-from-category';
import { QuestionWaterDto } from './water/dto.type';
import { QuestionWaterMapper } from './water/mapper';

const http = new HttpService({ baseURL: import.meta.env.VITE_API_IMPACT });

export async function submitImpactAnswer(
  projectId: number,
  answer: ImpactCo2Answer,
  submitMode: 'create' | 'edit',
): Promise<void> {
  switch (answer.category) {
    case 'ACCOMMODATION':
      await submitImpactAnswerOfType<
        QuestionAccommodationDto,
        ValuesAccommodation
      >(
        projectId,
        answer as ImpactCo2Answer<ValuesAccommodation>,
        QuestionAccommodationMapper.entityToDto,
        submitMode,
      );
      break;
    case 'ACCOMMODATION_PUBLIC':
      await submitImpactAnswerOfType<
        QuestionAccommodationPublicDto,
        ValuesAccommodationPublic
      >(
        projectId,
        answer as ImpactCo2Answer<ValuesAccommodationPublic>,
        QuestionAccommodationPublicMapper.entityToDto,
        submitMode,
      );
      break;
    case 'BUILDING':
      await submitImpactAnswerOfType<QuestionBuildingDto, ValuesBuilding>(
        projectId,
        answer as ImpactCo2Answer<ValuesBuilding>,
        QuestionBuildingMapper.entityToDto,
        submitMode,
      );
      break;
    case 'CARGO':
      await submitImpactAnswerOfType<QuestionCargoDto, ValuesCargo>(
        projectId,
        answer as ImpactCo2Answer<ValuesCargo>,
        QuestionCargoMapper.entityToDto,
        submitMode,
      );
      break;
    case 'CATERING_BEVERAGE':
      await submitImpactAnswerOfType<
        QuestionCateringBeverageDto,
        ValuesCateringBeverage
      >(
        projectId,
        answer as ImpactCo2Answer<ValuesCateringBeverage>,
        QuestionCateringBeverageMapper.entityToDto,
        submitMode,
      );
      break;
    case 'CATERING_MEAL':
      await submitImpactAnswerOfType<
        QuestionCateringMealDto,
        ValuesCateringMeal
      >(
        projectId,
        answer as ImpactCo2Answer<ValuesCateringMeal>,
        QuestionCateringMealMapper.entityToDto,
        submitMode,
      );
      break;
    case 'ENERGY':
      await submitImpactAnswerOfType<QuestionEnergyDto, ValuesEnergy>(
        projectId,
        answer as ImpactCo2Answer<ValuesEnergy>,
        QuestionEnergyMapper.entityToDto,
        submitMode,
      );
      break;
    case 'EQUIPMENT':
      await submitImpactAnswerOfType<QuestionEquipmentDto, ValuesEquipment>(
        projectId,
        answer as ImpactCo2Answer<ValuesEquipment>,
        QuestionEquipmentMapper.entityToDto,
        submitMode,
      );
      break;
    case 'GENERATOR':
      await submitImpactAnswerOfType<QuestionGeneratorDto, ValuesGenerator>(
        projectId,
        answer as ImpactCo2Answer<ValuesGenerator>,
        QuestionGeneratorMapper.entityToDto,
        submitMode,
      );
      break;
    case 'MOBILITY':
      await submitImpactAnswerOfType<QuestionMobilityDto, ValuesMobility>(
        projectId,
        answer as ImpactCo2Answer<ValuesMobility>,
        QuestionMobilityMapper.entityToDto,
        submitMode,
      );
      break;
    case 'MOBILITY_PUBLIC':
      await submitImpactAnswerOfType<
        QuestionMobilityPublicDto,
        ValuesMobilityPublic
      >(
        projectId,
        answer as ImpactCo2Answer<ValuesMobilityPublic>,
        QuestionMobilityPublicMapper.entityToDto,
        submitMode,
      );
      break;
    case 'NON_RECYCLABLE_WASTE':
      await submitImpactAnswerOfType<
        QuestionNonRecyclableWasteDto,
        ValuesNonRecyclableWaste
      >(
        projectId,
        answer as ImpactCo2Answer<ValuesNonRecyclableWaste>,
        QuestionNonRecyclableWasteMapper.entityToDto,
        submitMode,
      );
      break;
    case 'RECYCLABLE_WASTE':
      await submitImpactAnswerOfType<
        QuestionRecyclableWasteDto,
        ValuesRecyclableWaste
      >(
        projectId,
        answer as ImpactCo2Answer<ValuesRecyclableWaste>,
        QuestionRecyclableWasteMapper.entityToDto,
        submitMode,
      );
      break;
    case 'WATER':
      await submitImpactAnswerOfType<QuestionWaterDto, ValuesWater>(
        projectId,
        answer as ImpactCo2Answer<ValuesWater>,
        QuestionWaterMapper.entityToDto,
        submitMode,
      );
      break;
    case 'SCENOGRAPHY_NO_RECYCLABLE':
      await submitImpactAnswerOfType<
        QuestionScenographyNoRecyclableDto,
        ValuesScenographyNoRecyclable
      >(
        projectId,
        answer as ImpactCo2Answer<ValuesScenographyNoRecyclable>,
        QuestionScenographyNoRecyclableMapper.entityToDto,
        submitMode,
      );
      break;
    case 'SCENOGRAPHY_RECYCLABLE':
      await submitImpactAnswerOfType<
        QuestionScenographyRecyclableDto,
        ValuesScenographyRecyclable
      >(
        projectId,
        answer as ImpactCo2Answer<ValuesScenographyRecyclable>,
        QuestionScenographyRecyclableMapper.entityToDto,
        submitMode,
      );
      break;
    case 'BUILDING_HEATING':
      await submitImpactAnswerOfType<
        QuestionBuildingHeatingDto,
        ValuesBuildingHeating
      >(
        projectId,
        answer as ImpactCo2Answer<ValuesBuildingHeating>,
        QuestionBuildingHeatingMapper.entityToDto,
        submitMode,
      );
      break;
    case 'OFFICE_SUPPLIES_NEW':
      await submitImpactAnswerOfType<
        QuestionOfficeSuppliesNewDto,
        ValuesOfficeSuppliesNew
      >(
        projectId,
        answer as ImpactCo2Answer<ValuesOfficeSuppliesNew>,
        QuestionOfficeSuppliesNewMapper.entityToDto,
        submitMode,
      );
      break;
    case 'OFFICE_SUPPLIES_REFURBISH':
      await submitImpactAnswerOfType<
        QuestionOfficeSuppliesRefurbishDto,
        ValuesOfficeSuppliesRefurbish
      >(
        projectId,
        answer as ImpactCo2Answer<ValuesOfficeSuppliesRefurbish>,
        QuestionOfficeSuppliesRefurbishMapper.entityToDto,
        submitMode,
      );
      break;
    case 'TEAM_MOBILITY':
      await submitImpactAnswerOfType<
        QuestionTeamMobilityDto,
        ValuesTeamMobility
      >(
        projectId,
        answer as ImpactCo2Answer<ValuesTeamMobility>,
        QuestionTeamMobilityMapper.entityToDto,
        submitMode,
      );
      break;
    case 'DIGITAL':
      await submitImpactAnswerOfType<QuestionDigitalDto, ValuesDigital>(
        projectId,
        answer as ImpactCo2Answer<ValuesDigital>,
        QuestionDigitalMapper.entityToDto,
        submitMode,
      );
      break;
    default:
      throw 'unknown category';
  }
}

async function submitImpactAnswerOfType<TDto, TValues>(
  projectId: number,
  answer: ImpactCo2Answer<TValues>,
  entityToDtoMapper: (
    answer: ImpactCo2Answer<TValues>,
    projectId: number,
  ) => Omit<TDto, 'id' | 'userId' | 'isExternal'>,
  submitMode: 'create' | 'edit',
): Promise<void> {
  const data = entityToDtoMapper(answer, projectId);
  if (submitMode === 'create') {
    const url = buildUrl(answer.category);
    await http.post(url, data);
  } else if (submitMode === 'edit') {
    const url = `${buildUrl(answer.category)}/${answer.id}`;
    await http.patch(url, data);
  }
}
